import { useState } from "react";
import { Navigation } from "./Index/components/Navigation";
import { Footer } from "./Index/components/Footer";
import { useLanguage } from "@/contexts/LanguageContext";
import { translations } from "@/translations";
import { Button } from "@/components/ui/button";
import { MessageCircle, FileText, Users, CheckCircle } from "lucide-react";
import { OpenHouseBanner } from "@/components/OpenHouseBanner";
import { EnrollmentRequestForm } from "@/components/EnrollmentRequestForm";
import { motion } from "framer-motion";
import { openWhatsApp, WHATSAPP_MESSAGES } from "@/lib/whatsapp";
import { usePageMeta } from "@/hooks/usePageMeta";

const META: Record<string, { title: string; description: string }> = {
  es: {
    title: "Proceso de admisión | Gándara International School",
    description: "Cómo matricular a tu hijo en Gándara. Proceso sencillo: reserva una visita, conoce la escuela y únete a nuestra comunidad.",
  },
  en: {
    title: "Admissions Process | Gándara International School",
    description: "How to enroll your child at Gándara. Simple process: book a visit, get to know the school and join our community.",
  },
  pt: {
    title: "Processo de Admissão | Gándara International School",
    description: "Como matricular o seu filho na Gándara. Processo simples: reserve uma visita, conheça a escola e junte-se à nossa comunidade.",
  },
  fr: {
    title: "Processus d'Admission | Gándara International School",
    description: "Comment inscrire votre enfant à Gándara. Processus simple : réservez une visite, découvrez l'école et rejoignez notre communauté.",
  },
};

const Admissions = () => {
  const { language } = useLanguage();
  const t = translations[language].admissions;
  const [isBannerVisible, setIsBannerVisible] = useState(true);
  const meta = META[language] || META.es;
  usePageMeta({ title: meta.title, description: meta.description });

  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      <OpenHouseBanner onVisibilityChange={setIsBannerVisible} />

      <div className={`${isBannerVisible ? 'pt-[120px]' : 'pt-20'} pb-20`}>
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="max-w-4xl mx-auto"
          >
            {/* Hero */}
            <div className="text-center mb-12">
              <h1 className="text-5xl font-light text-primary mb-6">
                {t.title}
              </h1>
              <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
                {t.subtitle}
              </p>
            </div>

            {/* Enrollment Form — moved up for conversion */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.15 }}
              viewport={{ once: true }}
              className="mb-16"
            >
              <EnrollmentRequestForm />
            </motion.div>

            {/* Process steps */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              viewport={{ once: true }}
              className="mb-12"
            >
              <h2 className="text-3xl font-light text-primary mb-8 text-center">
                {t.process.title}
              </h2>

              <div className="space-y-6">
                {t.process.steps.map((step: any, index: number) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.5, delay: 0.1 * index }}
                    viewport={{ once: true }}
                    className="bg-card rounded-2xl p-6 shadow-lg flex gap-6"
                  >
                    <div className="flex-shrink-0">
                      <div className="w-12 h-12 rounded-full bg-accent/20 flex items-center justify-center">
                        <span className="text-2xl font-light text-primary">{index + 1}</span>
                      </div>
                    </div>
                    <div>
                      <h3 className="text-xl font-medium text-primary mb-2">
                        {step.title}
                      </h3>
                      <p className="text-muted-foreground">
                        {step.description}
                      </p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            {/* Requirements */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              viewport={{ once: true }}
              className="bg-card rounded-2xl p-8 shadow-lg mb-8"
            >
              <h2 className="text-3xl font-light text-primary mb-6 flex items-center gap-3">
                <FileText className="w-8 h-8 text-primary" />
                {t.requirements.title}
              </h2>
              <ul className="space-y-3">
                {t.requirements.items.map((item: string, index: number) => (
                  <li key={index} className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-primary mt-1 flex-shrink-0" />
                    <span className="text-muted-foreground">{item}</span>
                  </li>
                ))}
              </ul>
            </motion.div>

            {/* Visit / Contact CTAs */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.4 }}
              viewport={{ once: true }}
              className="grid md:grid-cols-2 gap-8 mb-8"
            >
              <div className="bg-gradient-to-br from-primary/10 to-accent/10 rounded-2xl p-8 text-center">
                <MessageCircle className="w-12 h-12 text-primary mx-auto mb-4" />
                <h3 className="text-xl font-medium text-primary mb-4">
                  {t.visit.title}
                </h3>
                <p className="text-muted-foreground mb-6">
                  {t.visit.description}
                </p>
                <Button
                  onClick={() => openWhatsApp(WHATSAPP_MESSAGES.bookVisit[language])}
                  className="bg-primary hover:bg-primary/90 text-primary-foreground px-6 py-3 rounded-lg shadow-lg transform transition-all duration-200 hover:scale-105 hover:shadow-xl"
                >
                  {t.visit.button}
                </Button>
              </div>

              <div className="bg-gradient-to-br from-accent/10 to-primary/10 rounded-2xl p-8 text-center">
                <Users className="w-12 h-12 text-primary mx-auto mb-4" />
                <h3 className="text-xl font-medium text-primary mb-4">
                  {t.contact.title}
                </h3>
                <p className="text-muted-foreground mb-6">
                  {t.contact.description}
                </p>
                <Button
                  asChild
                  variant="outline"
                  className="border-primary text-primary hover:bg-primary hover:text-primary-foreground px-6 py-3 rounded-lg shadow-lg transform transition-all duration-200 hover:scale-105"
                >
                  <a href="mailto:info@gandaraschool.com">
                    {t.contact.button}
                  </a>
                </Button>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default Admissions;
